/**
 * Hook to fetch and track pending users count for Admin navigation badge
 * Only fetches for authenticated Admin users
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppRoles } from '../services/authService';
import * as usersService from '../services/usersService';

// Polling interval in milliseconds (60 seconds)
const POLLING_INTERVAL = 60000;

/**
 * Custom hook to get the count of pending user registrations
 * @returns {Object} { pendingCount, loading, refresh }
 */
const usePendingUsersCount = () => {
  const { isAuthenticated, hasRole, authInitialized, hasConfirmedSession } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const isAdmin = authInitialized && hasConfirmedSession && isAuthenticated && hasRole(AppRoles.Admin);

  /**
   * Fetch pending users count from API
   */
  const fetchCount = useCallback(async () => {
    if (!isAdmin) {
      setPendingCount(0);
      return;
    }

    try {
      setLoading(true);
      // Fetch with minimal page size, we only need the totalCount
      const response = await usersService.getPendingUsers({ page: 1, pageSize: 1 });
      setPendingCount(response.totalCount || 0);
    } catch (error) {
      // Silently fail - badge is non-critical UI element
      console.debug('Failed to fetch pending users count:', error);
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(() => {
    fetchCount();
  }, [fetchCount]);

  // Initial fetch and polling setup
  useEffect(() => {
    if (isAdmin) {
      // Fetch immediately
      fetchCount();

      // Set up polling
      intervalRef.current = setInterval(fetchCount, POLLING_INTERVAL);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      setPendingCount(0);
    }
  }, [isAdmin, fetchCount]);

  return { pendingCount, loading, refresh };
};

export default usePendingUsersCount;
